import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Check, X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { 
  requestNotificationPermission, 
  getNotificationPermission,
  onForegroundMessage,
  initializeFirebase,
  isFirebaseEnabled
} from '../lib/firebase';
import { notificationsAPI } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { toast } from 'sonner';

export const NotificationPermissionBanner = ({ onDismiss }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [permission, setPermission] = useState(getNotificationPermission());
  const [dismissed, setDismissed] = useState(false);
  
  useEffect(() => {
    // Check if user already dismissed
    const wasDismissed = localStorage.getItem('notification_banner_dismissed');
    if (wasDismissed) {
      setDismissed(true);
    }
    
    // Update permission status
    setPermission(getNotificationPermission());
  }, []);
  
  const handleEnable = async () => {
    setLoading(true);
    try {
      const result = await requestNotificationPermission();
      
      if (result.granted && result.token && user) {
        // Register token with backend
        await notificationsAPI.registerToken(result.token);
        toast.success('¡Notificaciones activadas!');
        setPermission('granted');
      } else if (result.reason === 'denied') {
        toast.error('Permiso denegado. Puedes cambiarlo en la configuración del navegador.');
        setPermission('denied');
      } else if (result.reason === 'firebase_not_configured') {
        toast.info('Notificaciones habilitadas (modo demo)');
        setPermission('granted');
      }
      
      handleDismiss();
    } catch (error) {
      console.error('Error enabling notifications:', error);
      toast.error('Error al activar notificaciones');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDismiss = () => {
    localStorage.setItem('notification_banner_dismissed', 'true');
    setDismissed(true);
    onDismiss?.();
  };
  
  // Don't show if already granted, denied, or dismissed
  if (permission === 'granted' || permission === 'denied' || dismissed) {
    return null;
  }
  
  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-up">
      <Card className="card-elevated shadow-xl border-[#00BFA6]/20">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#00BFA6]/10 flex items-center justify-center flex-shrink-0">
              <Bell className="w-5 h-5 text-[#00BFA6]" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-slate-800 text-sm">
                Activa las notificaciones
              </h4>
              <p className="text-slate-500 text-xs mt-1">
                Recibe alertas de tu viaje, conductor cerca, entregas y más.
              </p>
              <div className="flex gap-2 mt-3">
                <Button
                  size="sm"
                  onClick={handleEnable}
                  disabled={loading}
                  className="h-8 bg-[#00BFA6] hover:bg-[#00887A] text-white text-xs"
                  data-testid="enable-notifications-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-3 h-3 mr-1" />
                      Activar
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleDismiss}
                  className="h-8 text-slate-500 text-xs"
                >
                  Más tarde
                </Button>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-slate-400 hover:text-slate-600 p-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Hook to handle foreground notifications
export const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  
  useEffect(() => {
    // Initialize Firebase
    initializeFirebase();
    
    // Listen for foreground messages
    const unsubscribe = onForegroundMessage((payload) => {
      const notification = {
        id: payload.data?.notification_id || Date.now().toString(),
        title: payload.notification?.title || 'Nueva notificación',
        body: payload.notification?.body || '',
        event_type: payload.data?.event_type,
        data: payload.data,
        timestamp: new Date()
      };
      
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
      
      // Show toast for foreground notification
      toast(notification.title, {
        description: notification.body,
        action: {
          label: 'Ver',
          onClick: () => {
            // Navigate based on event type
            handleNotificationClick(notification);
          }
        }
      });
    });
    
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);
  
  const handleNotificationClick = (notification) => {
    const eventType = notification.event_type || notification.data?.event_type;
    
    switch (eventType) {
      case 'service_assigned':
      case 'service_started':
      case 'driver_nearby':
        window.location.href = notification.data?.service_type === 'delivery' ? '/delivery' : '/ride';
        break;
      case 'service_completed':
      case 'rating_reminder':
        window.location.href = '/profile';
        break;
      case 'payment_received':
        window.location.href = '/wallet';
        break;
      default:
        break;
    }
  };
  
  const clearUnread = () => {
    setUnreadCount(0);
  };
  
  return {
    notifications,
    unreadCount,
    clearUnread,
    handleNotificationClick
  };
};

// Notification bell icon with badge
export const NotificationBell = ({ onClick }) => {
  const { unreadCount } = useNotifications();
  const [permission] = useState(getNotificationPermission());
  
  if (permission === 'denied') {
    return (
      <button 
        onClick={onClick}
        className="relative p-2 text-slate-400 hover:text-slate-600 transition-colors"
        title="Notificaciones bloqueadas"
      >
        <BellOff className="w-5 h-5" />
      </button>
    );
  }
  
  return (
    <button 
      onClick={onClick}
      className="relative p-2 text-slate-600 hover:text-[#00BFA6] transition-colors"
      data-testid="notification-bell"
    >
      <Bell className="w-5 h-5" />
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </button>
  );
};

export default NotificationPermissionBanner;
